// src/components/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useMemo } from "react";
// Imported from its own module rather than the hooks barrel: the barrel also
// re-exports hooks that now read this provider's context, so going through it
// would form an import cycle (providers -> hooks -> useShouldShowNav ->
// providers). Pointing straight at the leaf module keeps the graph acyclic.
import { useAuth } from "../hooks/auth/useAuth";
import { tokenMonitor } from "../services/Authentication/tokenMonitor";
import { sessionRestore } from "../services/Authentication/sessionRestore";
import { config } from "../config/env";

interface AuthContextType {
  user: any;
  loading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ 
    success: boolean; 
    error?: string; 
    message?: string; 
    requiresEmailVerification?: boolean; 
    data?: any;
    remainingAttempts?: number;
    lockoutExpiresAt?: Date;
    lockoutLevel?: number;
    timeUntilUnlock?: number;
    shouldRedirectToAdmin?: boolean;
    userRole?: string;
  }>;
  logout: () => Promise<void>;
  socialLogin: (provider: 'google' | 'facebook') => Promise<{ success: boolean; error?: string; data?: any }>;
  checkAuth: () => Promise<void>;
  // Session management
  getUserSessions: () => Promise<any[]>;
  revokeSession: (sessionId: string) => Promise<boolean>;
  revokeAllOtherSessions: () => Promise<boolean>;
  getCurrentSessionId: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useAuth();

  useEffect(() => {
    // Skip auth initialization if dev bypass is enabled
    if (config.dev.bypassAuth) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔓 Dev mode: Auth bypass enabled - skipping initialization');
      }
      return;
    }
    
    const initializeApp = async () => {
      try {
        const sessionRestored = await sessionRestore.initializeApp();
        
        if (sessionRestored) {
          // If session was restored, check auth to get user data
          await auth.checkAuth();
        }
      } catch (error) {
        console.error("App initialization failed:", error);
      }
    };
    
    initializeApp();
  }, []); // Empty dependency array - run only once on mount

  useEffect(() => {
    if (auth.isAuthenticated) {
      tokenMonitor.startMonitoring();
    } else {
      tokenMonitor.stopMonitoring();
    }

    return () => {
      tokenMonitor.stopMonitoring();
    };
  }, [auth.isAuthenticated]);

  // Memoize auth value to prevent unnecessary re-renders
  const authValue = useMemo(() => auth, [auth]);

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
